import { useState, useEffect, useCallback, useMemo } from 'react';
import {
    Box,
    Paper,
    Typography,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    Alert,
    Stack,
    LinearProgress,
    CircularProgress,
    TextField,
    TablePagination,
    Tooltip,
    Snackbar,
    Link,
} from '@mui/material';
import {
    CloudUpload as UploadIcon,
    Delete as DeleteIcon,
    Download as DownloadIcon,
} from '@mui/icons-material';
import { useDropzone } from 'react-dropzone';
import { Title, useGetIdentity } from 'react-admin';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { supabase } from '../providers/supabase/supabase';
import { usePapaParse } from '../misc/usePapaParse';
import { useBindTimesImport, BindTimesCsvRow } from './useBindTimesImport';

type BindTimesRow = {
    id: number;
    aip: string;
    states: string;
    bind_time: string;
    disclaimer: string | null;
    created_at: string;
};

type SortField = keyof Pick<BindTimesRow, 'aip' | 'states' | 'bind_time'>;

type SortDir = 'asc' | 'desc';

export const BindTimesPage = () => {
    const { identity } = useGetIdentity();
    const isAdmin = !!(identity as any)?.administrator;

    const [rows, setRows] = useState<BindTimesRow[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [uploadOpen, setUploadOpen] = useState(false);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);
    const [sortField, setSortField] = useState<SortField>('aip');
    const [sortDir, setSortDir] = useState<SortDir>('asc');
    const [snackMsg, setSnackMsg] = useState('');

    // Disclaimer dialog state
    const [disclaimerRow, setDisclaimerRow] = useState<BindTimesRow | null>(
        null
    );

    const fetchData = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const PAGE_SIZE = 1000;
            let allData: BindTimesRow[] = [];
            let from = 0;
            let hasMore = true;

            while (hasMore) {
                const { data, error: fetchError } = await supabase
                    .from('bind_times')
                    .select('*')
                    .order('aip', { ascending: true })
                    .order('states', { ascending: true })
                    .range(from, from + PAGE_SIZE - 1);

                if (fetchError) throw fetchError;
                const page = (data as BindTimesRow[]) ?? [];
                allData = allData.concat(page);
                hasMore = page.length === PAGE_SIZE;
                from += PAGE_SIZE;
            }

            setRows(allData);
        } catch (err: any) {
            console.error('Failed to fetch bind times:', err);
            setError('Failed to load bind times data.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const displayRows = useMemo(() => {
        return [...rows].sort((a, b) => {
            const aVal = a[sortField];
            const bVal = b[sortField];

            if (aVal == null && bVal == null) return 0;
            if (aVal == null) return 1;
            if (bVal == null) return -1;

            const cmp = String(aVal).localeCompare(String(bVal));
            return sortDir === 'asc' ? cmp : -cmp;
        });
    }, [rows, sortField, sortDir]);

    const handleSort = (field: SortField) => {
        if (sortField === field) {
            setSortDir(prev => (prev === 'asc' ? 'desc' : 'asc'));
        } else {
            setSortField(field);
            setSortDir('asc');
        }
    };

    const handleClearAll = async () => {
        if (
            !window.confirm(
                'Are you sure you want to delete ALL bind time records? This cannot be undone.'
            )
        )
            return;

        try {
            const { error: delError } = await supabase
                .from('bind_times')
                .delete()
                .neq('id', 0);
            if (delError) throw delError;
            setRows([]);
        } catch (err: any) {
            console.error('Failed to clear bind times:', err);
            setError('Failed to clear bind times data.');
        }
    };

    const handleCellSave = async (id: number, field: string, value: any) => {
        const { error: updateErr } = await supabase
            .from('bind_times')
            .update({ [field]: value })
            .eq('id', id);

        if (updateErr) {
            console.error('Failed to update:', updateErr);
            setSnackMsg('Failed to save change.');
            return;
        }

        setRows(prev =>
            prev.map(r => (r.id === id ? { ...r, [field]: value } : r))
        );
        setSnackMsg('Saved');
    };

    const handleDownloadSample = () => {
        const csv =
            'AIP,States,Bind Time\n' +
            'Great American,MT,48 hours\n' +
            'NAU,ID,24 hours\n';
        const blob = new Blob([csv], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'bind_times_sample.csv';
        a.click();
        URL.revokeObjectURL(url);
    };

    const columns: {
        field: SortField;
        label: string;
        width?: number;
    }[] = [
        { field: 'aip', label: 'AIP' },
        { field: 'states', label: 'States', width: 100 },
        { field: 'bind_time', label: 'Bind Time' },
    ];

    return (
        <Box sx={{ p: 3, maxWidth: 1400, mx: 'auto' }}>
            <Title title="Bind Times" />

            <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ mb: 2 }}
            >
                <Typography variant="h5" fontWeight={600}>
                    Bind Times
                </Typography>
                {isAdmin && (
                    <Stack direction="row" spacing={1}>
                        <Tooltip title="Download sample CSV">
                            <Button
                                variant="outlined"
                                size="small"
                                startIcon={<DownloadIcon />}
                                onClick={handleDownloadSample}
                            >
                                Sample CSV
                            </Button>
                        </Tooltip>
                        {rows.length > 0 && (
                            <Button
                                variant="outlined"
                                color="error"
                                size="small"
                                startIcon={<DeleteIcon />}
                                onClick={handleClearAll}
                            >
                                Clear All
                            </Button>
                        )}
                        <Button
                            variant="contained"
                            startIcon={<UploadIcon />}
                            onClick={() => setUploadOpen(true)}
                        >
                            Upload CSV
                        </Button>
                    </Stack>
                )}
            </Stack>

            {rows.length > 0 && (
                <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 2 }}
                >
                    {displayRows.length} record
                    {displayRows.length !== 1 ? 's' : ''}
                </Typography>
            )}

            {error && (
                <Alert
                    severity="error"
                    sx={{ mb: 2 }}
                    onClose={() => setError('')}
                >
                    {error}
                </Alert>
            )}

            {loading ? (
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        p: 6,
                    }}
                >
                    <CircularProgress />
                </Box>
            ) : rows.length === 0 ? (
                <Paper
                    sx={{
                        p: 6,
                        textAlign: 'center',
                        color: 'text.secondary',
                    }}
                >
                    <UploadIcon sx={{ fontSize: 48, mb: 2, opacity: 0.5 }} />
                    <Typography variant="h6" gutterBottom>
                        No bind times data yet
                    </Typography>
                    {isAdmin ? (
                        <>
                            <Typography variant="body2" sx={{ mb: 2 }}>
                                Upload a CSV file to populate the bind times
                                table.
                            </Typography>
                            <Button
                                variant="contained"
                                startIcon={<UploadIcon />}
                                onClick={() => setUploadOpen(true)}
                            >
                                Upload CSV
                            </Button>
                        </>
                    ) : (
                        <Typography variant="body2">
                            An admin has not uploaded bind times data yet.
                        </Typography>
                    )}
                </Paper>
            ) : (
                <Paper>
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    {columns.map(col => (
                                        <TableCell
                                            key={col.field}
                                            sx={{
                                                fontWeight: 700,
                                                ...(col.width
                                                    ? {
                                                          width: col.width,
                                                      }
                                                    : {}),
                                            }}
                                            sortDirection={
                                                sortField === col.field
                                                    ? sortDir
                                                    : false
                                            }
                                        >
                                            <TableSortLabel
                                                active={sortField === col.field}
                                                direction={
                                                    sortField === col.field
                                                        ? sortDir
                                                        : 'asc'
                                                }
                                                onClick={() =>
                                                    handleSort(col.field)
                                                }
                                            >
                                                {col.label}
                                            </TableSortLabel>
                                        </TableCell>
                                    ))}
                                    <TableCell sx={{ fontWeight: 700 }}>
                                        Disclaimer
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {displayRows
                                    .slice(
                                        page * rowsPerPage,
                                        page * rowsPerPage + rowsPerPage
                                    )
                                    .map(row => (
                                        <EditableRow
                                            key={row.id}
                                            row={row}
                                            editable={isAdmin}
                                            onSave={handleCellSave}
                                            onViewDisclaimer={() =>
                                                setDisclaimerRow(row)
                                            }
                                        />
                                    ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination
                        component="div"
                        count={displayRows.length}
                        page={page}
                        onPageChange={(_, newPage) => setPage(newPage)}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={e => {
                            setRowsPerPage(parseInt(e.target.value, 10));
                            setPage(0);
                        }}
                        rowsPerPageOptions={[10, 25, 50, 100]}
                    />
                </Paper>
            )}

            <UploadDialog
                open={uploadOpen}
                onClose={() => setUploadOpen(false)}
                onComplete={() => {
                    setUploadOpen(false);
                    fetchData();
                }}
            />

            {disclaimerRow && (
                <DisclaimerDialog
                    row={disclaimerRow}
                    isAdmin={isAdmin}
                    onClose={() => setDisclaimerRow(null)}
                    onSave={async (html: string) => {
                        await handleCellSave(
                            disclaimerRow.id,
                            'disclaimer',
                            html || null
                        );
                        setDisclaimerRow(prev =>
                            prev
                                ? {
                                      ...prev,
                                      disclaimer: html || null,
                                  }
                                : null
                        );
                    }}
                />
            )}

            <Snackbar
                open={!!snackMsg}
                autoHideDuration={2000}
                onClose={() => setSnackMsg('')}
                message={snackMsg}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'center',
                }}
            />
        </Box>
    );
};

// ---------- Editable Row ----------

function EditableRow({
    row,
    editable,
    onSave,
    onViewDisclaimer,
}: {
    row: BindTimesRow;
    editable: boolean;
    onSave: (id: number, field: string, value: any) => void;
    onViewDisclaimer: () => void;
}) {
    if (!editable) {
        return (
            <TableRow hover>
                <TableCell>{row.aip || '\u2014'}</TableCell>
                <TableCell>{row.states || '\u2014'}</TableCell>
                <TableCell>{row.bind_time || '\u2014'}</TableCell>
                <TableCell>
                    <Link
                        component="button"
                        variant="body2"
                        onClick={onViewDisclaimer}
                        sx={{ cursor: 'pointer' }}
                    >
                        View
                    </Link>
                </TableCell>
            </TableRow>
        );
    }

    return (
        <TableRow hover>
            <EditableTextCell
                value={row.aip}
                onSave={val => onSave(row.id, 'aip', val)}
            />
            <EditableTextCell
                value={row.states}
                onSave={val => onSave(row.id, 'states', val)}
            />
            <EditableTextCell
                value={row.bind_time}
                onSave={val => onSave(row.id, 'bind_time', val)}
            />
            <TableCell>
                <Link
                    component="button"
                    variant="body2"
                    onClick={onViewDisclaimer}
                    sx={{ cursor: 'pointer' }}
                >
                    View
                </Link>
            </TableCell>
        </TableRow>
    );
}

// ---------- Editable Text Cell ----------

function EditableTextCell({
    value,
    onSave,
}: {
    value: string;
    onSave: (val: string) => void;
}) {
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState(value);

    const commit = () => {
        setEditing(false);
        if (draft !== value) {
            onSave(draft);
        }
    };

    if (editing) {
        return (
            <TableCell sx={{ p: 0.5 }}>
                <TextField
                    value={draft}
                    onChange={e => setDraft(e.target.value)}
                    onBlur={commit}
                    onKeyDown={e => {
                        if (e.key === 'Enter') commit();
                        if (e.key === 'Escape') {
                            setDraft(value);
                            setEditing(false);
                        }
                    }}
                    size="small"
                    variant="standard"
                    autoFocus
                    fullWidth
                    sx={{
                        '& input': { fontSize: '0.875rem' },
                    }}
                />
            </TableCell>
        );
    }

    return (
        <TableCell
            onClick={() => {
                setDraft(value);
                setEditing(true);
            }}
            sx={{
                cursor: 'pointer',
                '&:hover': { bgcolor: 'action.hover' },
            }}
        >
            {value || '\u2014'}
        </TableCell>
    );
}

// ---------- Disclaimer Dialog ----------

const editorStyles = {
    '& .tiptap': {
        outline: 'none',
        minHeight: 200,
        p: 2,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        '& p': { m: 0, mb: 1 },
        '& h1, & h2, & h3': { mt: 2, mb: 1 },
        '& ul, & ol': { pl: 3 },
        '& blockquote': {
            borderLeft: '3px solid',
            borderColor: 'divider',
            pl: 2,
            ml: 0,
            color: 'text.secondary',
        },
    },
} as const;

function DisclaimerDialog({
    row,
    isAdmin,
    onClose,
    onSave,
}: {
    row: BindTimesRow;
    isAdmin: boolean;
    onClose: () => void;
    onSave: (html: string) => Promise<void>;
}) {
    const [saving, setSaving] = useState(false);

    const editor = useEditor({
        extensions: [StarterKit],
        content: row.disclaimer || '',
        editable: isAdmin,
    });

    const handleSave = async () => {
        if (!editor) return;
        setSaving(true);
        try {
            await onSave(editor.getHTML());
            onClose();
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>
                Disclaimer &mdash; {row.aip} ({row.states})
            </DialogTitle>
            <DialogContent>
                {isAdmin ? (
                    <Box sx={{ mt: 1 }}>
                        <EditorToolbar editor={editor} />
                        <Box sx={editorStyles}>
                            <EditorContent editor={editor} />
                        </Box>
                    </Box>
                ) : row.disclaimer ? (
                    <Box
                        sx={{
                            mt: 1,
                            '& p': { m: 0, mb: 1 },
                            '& h1, & h2, & h3': {
                                mt: 2,
                                mb: 1,
                            },
                            '& ul, & ol': { pl: 3 },
                            '& blockquote': {
                                borderLeft: '3px solid',
                                borderColor: 'divider',
                                pl: 2,
                                ml: 0,
                                color: 'text.secondary',
                            },
                        }}
                        dangerouslySetInnerHTML={{
                            __html: row.disclaimer,
                        }}
                    />
                ) : (
                    <Typography color="text.secondary" sx={{ mt: 1 }}>
                        No disclaimer has been added yet.
                    </Typography>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>
                    {isAdmin ? 'Cancel' : 'Close'}
                </Button>
                {isAdmin && (
                    <Button
                        variant="contained"
                        onClick={handleSave}
                        disabled={saving}
                    >
                        {saving ? 'Saving...' : 'Save'}
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
}

// ---------- Editor Toolbar ----------

function EditorToolbar({ editor }: { editor: ReturnType<typeof useEditor> }) {
    if (!editor) return null;

    const btnSx = (active: boolean) => ({
        minWidth: 36,
        fontWeight: active ? 700 : 400,
        bgcolor: active ? 'action.selected' : 'transparent',
    });

    return (
        <Stack
            direction="row"
            spacing={0.5}
            sx={{
                mb: 1,
                flexWrap: 'wrap',
                gap: 0.5,
            }}
        >
            <Button
                size="small"
                variant="outlined"
                sx={btnSx(editor.isActive('bold'))}
                onClick={() => editor.chain().focus().toggleBold().run()}
            >
                B
            </Button>
            <Button
                size="small"
                variant="outlined"
                sx={{
                    ...btnSx(editor.isActive('italic')),
                    fontStyle: 'italic',
                }}
                onClick={() => editor.chain().focus().toggleItalic().run()}
            >
                I
            </Button>
            <Button
                size="small"
                variant="outlined"
                sx={btnSx(editor.isActive('heading', { level: 2 }))}
                onClick={() =>
                    editor.chain().focus().toggleHeading({ level: 2 }).run()
                }
            >
                H2
            </Button>
            <Button
                size="small"
                variant="outlined"
                sx={btnSx(editor.isActive('heading', { level: 3 }))}
                onClick={() =>
                    editor.chain().focus().toggleHeading({ level: 3 }).run()
                }
            >
                H3
            </Button>
            <Button
                size="small"
                variant="outlined"
                sx={btnSx(editor.isActive('bulletList'))}
                onClick={() => editor.chain().focus().toggleBulletList().run()}
            >
                List
            </Button>
            <Button
                size="small"
                variant="outlined"
                sx={btnSx(editor.isActive('orderedList'))}
                onClick={() => editor.chain().focus().toggleOrderedList().run()}
            >
                1.
            </Button>
            <Button
                size="small"
                variant="outlined"
                sx={btnSx(editor.isActive('blockquote'))}
                onClick={() => editor.chain().focus().toggleBlockquote().run()}
            >
                &ldquo;
            </Button>
        </Stack>
    );
}

// ---------- Upload Dialog ----------

function UploadDialog({
    open,
    onClose,
    onComplete,
}: {
    open: boolean;
    onClose: () => void;
    onComplete: () => void;
}) {
    const { processBatch } = useBindTimesImport();
    const { importer, parseCsv, reset } = usePapaParse<BindTimesCsvRow>({
        batchSize: 50,
        processBatch,
    });

    const onDrop = useCallback(
        (accepted: File[]) => {
            if (accepted.length > 0) {
                parseCsv(accepted[0]);
            }
        },
        [parseCsv]
    );

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        accept: {
            'text/csv': ['.csv'],
            'application/vnd.ms-excel': ['.csv'],
        },
        multiple: false,
        disabled: importer.state === 'parsing' || importer.state === 'running',
    });

    const handleClose = () => {
        if (importer.state === 'complete') {
            onComplete();
        } else {
            onClose();
        }
        reset();
    };

    const progress =
        importer.state === 'running' || importer.state === 'complete'
            ? importer.rowCount > 0
                ? (importer.importCount / importer.rowCount) * 100
                : 0
            : 0;

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle>Upload Bind Times CSV</DialogTitle>
            <DialogContent>
                <Stack spacing={2} sx={{ mt: 1 }}>
                    <Alert severity="info">
                        Expected columns: <strong>AIP</strong>,{' '}
                        <strong>States</strong>, <strong>Bind Time</strong>
                    </Alert>

                    <Box
                        {...getRootProps()}
                        sx={{
                            border: '2px dashed',
                            borderColor: isDragActive ? 'primary.main' : '#ccc',
                            borderRadius: 2,
                            p: 4,
                            textAlign: 'center',
                            cursor:
                                importer.state === 'running'
                                    ? 'default'
                                    : 'pointer',
                            bgcolor: isDragActive
                                ? 'action.hover'
                                : 'transparent',
                            transition: 'all 0.2s',
                        }}
                    >
                        <input {...getInputProps()} />
                        <UploadIcon
                            sx={{
                                fontSize: 40,
                                color: 'text.secondary',
                                mb: 1,
                            }}
                        />
                        <Typography>
                            {isDragActive
                                ? 'Drop the CSV file here...'
                                : 'Drag & drop a CSV file here, or click to select'}
                        </Typography>
                    </Box>

                    {importer.state === 'parsing' && (
                        <Stack direction="row" spacing={1} alignItems="center">
                            <CircularProgress size={20} />
                            <Typography variant="body2">
                                Parsing CSV...
                            </Typography>
                        </Stack>
                    )}

                    {(importer.state === 'running' ||
                        importer.state === 'complete') && (
                        <Box>
                            <Stack
                                direction="row"
                                justifyContent="space-between"
                                sx={{ mb: 0.5 }}
                            >
                                <Typography variant="body2">
                                    {importer.state === 'complete'
                                        ? 'Upload complete!'
                                        : `Importing ${importer.importCount} of ${importer.rowCount} rows...`}
                                </Typography>
                                {importer.remainingTime != null && (
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                    >
                                        ~
                                        {Math.ceil(
                                            importer.remainingTime / 1000
                                        )}
                                        s remaining
                                    </Typography>
                                )}
                            </Stack>
                            <LinearProgress
                                variant="determinate"
                                value={progress}
                                sx={{
                                    height: 8,
                                    borderRadius: 4,
                                }}
                            />
                            <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
                                <Typography
                                    variant="body2"
                                    color="success.main"
                                >
                                    {'\u2713'} {importer.importCount} imported
                                </Typography>
                                {importer.errorCount > 0 && (
                                    <Typography
                                        variant="body2"
                                        color="error.main"
                                    >
                                        {'\u2717'} {importer.errorCount} errors
                                    </Typography>
                                )}
                            </Stack>
                        </Box>
                    )}

                    {importer.state === 'error' && (
                        <Alert severity="error">
                            Failed to parse CSV: {importer.error.message}
                        </Alert>
                    )}
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose}>
                    {importer.state === 'complete' ? 'Done' : 'Cancel'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default BindTimesPage;
